import { useEffect, useState } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import MainTabsNav from '../components/MainTabsNav';

export default function EmailVerifiedPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const verifyEmail = async () => {
      // URL query parameter에서 success 확인 (서버 리다이렉트 결과)
      const querySuccess = searchParams.get('success');
      const queryError = searchParams.get('error');

      if (querySuccess === 'true') {
        setSuccess(true);
        setIsLoading(false);
        return;
      }

      if (querySuccess === 'false' && queryError !== 'invalid_token') {
        setError(queryError);
        setIsLoading(false);
        return;
      }

      // Hash fragment에서 토큰 확인 (Supabase 이메일 인증 링크)
      // URL: /auth/verify-email#access_token=...&type=signup
      const hash = location.hash;
      if (hash) {
        const hashParams = new URLSearchParams(hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const type = hashParams.get('type');

        if (accessToken && type === 'signup') {
          try {
            // 토큰으로 사용자 이메일 인증 완료 확인
            // access_token이 있다는 것은 Supabase가 이미 인증을 완료했다는 의미
            // 우리는 이를 확인만 하면 됨
            console.log('Email verification successful via hash fragment');
            setSuccess(true);
            setIsLoading(false);

            // URL에서 hash 제거 (보안)
            window.history.replaceState(null, '', '/auth/verify-email?success=true');
            return;
          } catch (err) {
            console.error('Error verifying email:', err);
            setError('이메일 인증 처리 중 오류가 발생했습니다.');
            setIsLoading(false);
            return;
          }
        }
      }

      // 토큰도 없고 success 파라미터도 없으면 에러
      if (!querySuccess && !hash) {
        setError('잘못된 접근입니다.');
      } else if (queryError === 'invalid_token') {
        // invalid_token 에러지만 hash에 토큰이 있으면 성공으로 처리
        const hashParams = new URLSearchParams(location.hash.substring(1));
        if (hashParams.get('access_token')) {
          setSuccess(true);
          window.history.replaceState(null, '', '/auth/verify-email?success=true');
        } else {
          setError(queryError);
        }
      }

      setIsLoading(false);
    };

    verifyEmail();
  }, [searchParams, location.hash]);

  // 로딩 중
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <MainTabsNav className="mt-16" />

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
            <p className="text-gray-600">이메일 인증을 확인하고 있습니다...</p>
          </div>
        </main>
      </div>
    );
  }

  // 인증 성공
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <MainTabsNav className="mt-16" />

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="max-w-md w-full">
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
              {/* Success Icon */}
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>

              {/* Title */}
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                이메일 인증 완료!
              </h1>

              {/* Description */}
              <p className="text-gray-600 mb-8">
                이메일 인증이 완료되었습니다.<br />
                이제 로그인하여 스탬핏을 이용할 수 있습니다.
              </p>

              {/* Login Button */}
              <Link
                to="/login"
                className="block w-full px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors text-center"
              >
                로그인하러 가기
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // 인증 실패
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <MainTabsNav className="mt-16" />

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            {/* Error Icon */}
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>

            {/* Title */}
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              인증에 실패했습니다
            </h1>

            {/* Error Message */}
            <p className="text-gray-600 mb-2">
              이메일 인증 중 문제가 발생했습니다.
            </p>
            {error && (
              <p className="text-sm text-red-600 bg-red-50 rounded-lg p-3 mb-6">
                {decodeURIComponent(error)}
              </p>
            )}

            {/* Possible reasons */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm font-medium text-gray-700 mb-2">가능한 원인:</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• 인증 링크가 만료되었습니다 (24시간)</li>
                <li>• 이미 인증이 완료된 계정입니다</li>
                <li>• 잘못된 인증 링크입니다</li>
              </ul>
            </div>

            {/* Retry Button */}
            <Link
              to="/login"
              className="block w-full px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors text-center mb-4"
            >
              로그인 페이지로 이동
            </Link>

            <p className="text-xs text-gray-500">
              문제가 계속되면 로그인 페이지에서 인증 이메일을 재발송해주세요.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
